import { create } from "zustand";

import {
  api,
  type QueueJob,
  type QueueJobDiagnostics,
  type QueueJobStatus,
  type QueueJobType,
  type QueueSummary,
} from "@/lib/api";

export interface QueueFilters {
  statuses: QueueJobStatus[];
  jobType: QueueJobType | null;
  priority: number | null;
  q: string;
  from: string;
  to: string;
}

interface QueuePagination {
  offset: number;
  limit: number;
}

interface QueueState {
  jobs: QueueJob[];
  total: number;
  summary: QueueSummary | null;
  selectedJobId: string | null;
  diagnosticsByJobId: Record<string, QueueJobDiagnostics | undefined>;
  filters: QueueFilters;
  pagination: QueuePagination;
  loading: boolean;
  diagnosticsLoading: boolean;
  summaryLoading: boolean;
  error: string | null;
  autoRefresh: boolean;

  fetchJobs: (opts?: FetchOptions) => Promise<void>;
  fetchSummary: (opts?: FetchOptions) => Promise<void>;
  fetchJobDiagnostics: (id: string) => Promise<void>;
  setFilters: (filters: Partial<QueueFilters>) => void;
  resetFilters: () => void;
  setSelectedJobId: (id: string | null) => void;
  setAutoRefresh: (enabled: boolean) => void;
  nextPage: () => void;
  prevPage: () => void;
  retryJob: (id: string) => Promise<void>;
  cancelJob: (id: string) => Promise<void>;
  updateJobPriority: (id: string, priority: number) => Promise<void>;
}

interface FetchOptions {
  signal?: AbortSignal;
  /** Poll de fundo: não acende o indicador de carregando. */
  silent?: boolean;
}

/** Aborto não é erro: quem cancelou foi a própria tela, ao sair ou recarregar. */
function foiAbortado(err: unknown): boolean {
  return err instanceof DOMException && (err.name === "AbortError" || err.name === "TimeoutError");
}

/**
 * Mantém o objeto anterior de cada job que voltou igual.
 *
 * O poll de 5 s substitui o array inteiro; sem isto toda linha da tabela
 * re-renderiza a cada volta, mesmo quando nada mudou na fila. Com a identidade
 * preservada, o `memo` de `QueueRow` corta o render de quem não mudou.
 */
function preservarIdentidade(anteriores: QueueJob[], novos: QueueJob[]): QueueJob[] {
  if (anteriores.length === 0) return novos;
  const porId = new Map(anteriores.map((job) => [job.id, job]));
  return novos.map((novo) => {
    const antigo = porId.get(novo.id);
    if (!antigo) return novo;
    const chaves = Object.keys(novo) as (keyof QueueJob)[];
    const igual =
      chaves.length === Object.keys(antigo).length &&
      chaves.every((chave) => antigo[chave] === novo[chave]);
    return igual ? antigo : novo;
  });
}

const DEFAULT_FILTERS: QueueFilters = {
  statuses: [],
  jobType: null,
  priority: null,
  q: "",
  from: "",
  to: "",
};

export const useQueueStore = create<QueueState>((set, get) => ({
  jobs: [],
  total: 0,
  summary: null,
  selectedJobId: null,
  diagnosticsByJobId: {},
  filters: { ...DEFAULT_FILTERS },
  pagination: { offset: 0, limit: 50 },
  loading: false,
  diagnosticsLoading: false,
  summaryLoading: false,
  error: null,
  autoRefresh: true,

  async fetchJobs(opts) {
    const { filters, pagination } = get();
    // Um `set` na entrada só quando a tela precisa mostrar que está carregando;
    // o poll de fundo não pisca nada e termina com um `set` único.
    if (!opts?.silent) set({ loading: true, error: null });
    try {
      const resp = await api.listQueueJobs(
        {
          status: filters.statuses.length > 0 ? filters.statuses : undefined,
          type: filters.jobType ?? undefined,
          priority: filters.priority ?? undefined,
          q: filters.q || undefined,
          from: filters.from || undefined,
          to: filters.to || undefined,
          offset: pagination.offset,
          limit: pagination.limit,
        },
        opts?.signal,
      );
      set((state) => ({
        jobs: preservarIdentidade(state.jobs, resp.items),
        total: resp.total,
        loading: false,
        error: null,
      }));
    } catch (err) {
      if (foiAbortado(err)) {
        set({ loading: false });
        return;
      }
      set({
        error: err instanceof Error ? err.message : "Erro ao carregar jobs.",
        loading: false,
      });
    }
  },

  async fetchSummary(opts) {
    if (!opts?.silent) set({ summaryLoading: true });
    try {
      const summary = await api.getQueueSummary(opts?.signal);
      set({ summary, summaryLoading: false });
    } catch {
      // Falha de resumo não é crítica: mantém o valor anterior na tela.
      set({ summaryLoading: false });
    }
  },

  async fetchJobDiagnostics(id) {
    set({ diagnosticsLoading: true });
    try {
      const diagnostics = await api.getQueueJobDiagnostics(id);
      set((state) => ({
        diagnosticsByJobId: { ...state.diagnosticsByJobId, [id]: diagnostics },
      }));
    } catch (err) {
      set({ error: err instanceof Error ? err.message : "Erro ao carregar diagnóstico." });
    } finally {
      set({ diagnosticsLoading: false });
    }
  },

  setFilters(filters) {
    set((state) => ({
      filters: { ...state.filters, ...filters },
      pagination: { ...state.pagination, offset: 0 },
    }));
  },

  resetFilters() {
    set({
      filters: { ...DEFAULT_FILTERS },
      pagination: { offset: 0, limit: get().pagination.limit },
    });
  },

  setSelectedJobId(id) {
    set({ selectedJobId: id });
  },

  setAutoRefresh(enabled) {
    set({ autoRefresh: enabled });
  },

  nextPage() {
    const { pagination, total } = get();
    const next = pagination.offset + pagination.limit;
    if (next < total) {
      set({ pagination: { ...pagination, offset: next } });
    }
  },

  prevPage() {
    const { pagination } = get();
    const prev = Math.max(0, pagination.offset - pagination.limit);
    set({ pagination: { ...pagination, offset: prev } });
  },

  async retryJob(id) {
    await api.retryJob(id);
    await get().fetchJobs();
    await get().fetchSummary();
  },

  async cancelJob(id) {
    await api.cancelJob(id);
    await get().fetchJobs();
    await get().fetchSummary();
  },

  async updateJobPriority(id, priority) {
    await api.updateJobPriority(id, priority);
    await get().fetchJobs();
    await get().fetchSummary();
  },
}));
